import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScrollLock } from "../../lib/useScrollLock";
import { auditApi, type AuditAction, type AuditLogRow } from "../../api/audit";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { Pagination } from "../../components/Pagination";
import { DateTimeInput } from "../../components/DateInput";
import { Select } from "../../components/Select";

const ACTIONS: AuditAction[] = [
  "CREATE", "UPDATE", "DELETE",
  "LOGIN", "LOGOUT", "LOGIN_FAILED",
  "INVITE",
  "PASSWORD_RESET_REQUEST", "PASSWORD_RESET_COMPLETE",
  "TOKEN_REFRESH", "ACCOUNT_LOCKED", "ACCOUNT_UNLOCKED",
];

// Entity types the backend writes audit rows for. Phase-1 domain entities
// (Position, PartType, Asset, Location) join this list as their modules land.
const ENTITY_TYPES = ["User", "Invitation"];

const PAGE_SIZE = 25;

export default function AdminAuditLogPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const [rows, setRows] = useState<AuditLogRow[] | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [actionFilter, setActionFilter] = useState<AuditAction | "">("");
  const [entityFilter, setEntityFilter] = useState<string>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openRow, setOpenRow] = useState<AuditLogRow | null>(null);

  async function load() {
    try {
      const result = await auditApi.search({
        action: actionFilter || undefined,
        entityType: entityFilter || undefined,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(to).toISOString() : undefined,
        page,
        size: PAGE_SIZE,
      });
      setRows(result.content);
      setTotalPages(result.totalPages);
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  useEffect(() => { setPage(0); }, [actionFilter, entityFilter, from, to]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [actionFilter, entityFilter, from, to, page]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-primary)" }}>{t("admin.auditLog.title", "Audit log")}</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={load} style={secondary}>{t("admin.auditLog.refresh", "Osveži")}</button>
        </div>
      </header>

      <div style={filterRow}>
        <Field label={t("admin.auditLog.field.action", "Radnja")}>
          <Select<AuditAction | "">
            value={actionFilter}
            onChange={setActionFilter}
            ariaLabel={t("admin.auditLog.field.action", "Radnja")}
            options={[
              { value: "", label: t("admin.auditLog.all", "Sve") },
              ...ACTIONS.map((a) => ({ value: a, label: a })),
            ]}
          />
        </Field>
        <Field label={t("admin.auditLog.field.entityType", "Tip entiteta")}>
          <Select<string>
            value={entityFilter}
            onChange={setEntityFilter}
            ariaLabel={t("admin.auditLog.field.entityType", "Tip entiteta")}
            options={[
              { value: "", label: t("admin.auditLog.all", "Sve") },
              ...ENTITY_TYPES.map((tp) => ({ value: tp, label: tp })),
            ]}
          />
        </Field>
        <Field label={t("admin.auditLog.field.from", "Od")}>
          <DateTimeInput value={from} onChange={setFrom} ariaLabel={t("admin.auditLog.field.from", "Od")} />
        </Field>
        <Field label={t("admin.auditLog.field.to", "Do")}>
          <DateTimeInput value={to} onChange={setTo} ariaLabel={t("admin.auditLog.field.to", "Do")} />
        </Field>
      </div>

      {!rows ? (
        <p style={{ color: "var(--ink-3)" }}>{t("common.loading", "Učitavanje…")}</p>
      ) : rows.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.auditLog.empty", "Nema stavki koje se podudaraju.")}</p>
      ) : (
        <div className="tbl-wrap">
          <table className="list-card" style={tbl}>
            <thead>
              <tr>
                <th style={th}>{t("admin.auditLog.col.when", "Kada")}</th>
                <th style={th}>{t("admin.auditLog.col.actor", "Izvršilac")}</th>
                <th style={th}>{t("admin.auditLog.col.action", "Radnja")}</th>
                <th style={th}>{t("admin.auditLog.col.entity", "Entitet")}</th>
                <th style={th}>{t("admin.auditLog.col.ip", "IP")}</th>
                <th style={th}>{t("admin.auditLog.col.request", "Zahtev")}</th>
                <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Akcije")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={td} data-label={t("admin.auditLog.col.when", "Kada")}>{new Date(r.createdAt).toLocaleString("sr-Latn-RS")}</td>
                  <td style={td} data-label={t("admin.auditLog.col.actor", "Izvršilac")}>{r.actorName ?? <span style={{ color: "var(--ink-4)" }}>{t("admin.auditLog.system", "sistem")}</span>}</td>
                  <td style={td} data-label={t("admin.auditLog.col.action", "Radnja")}><ActionBadge action={r.action} /></td>
                  <td style={td} data-label={t("admin.auditLog.col.entity", "Entitet")}>
                    <code style={{ fontSize: 11 }}>{r.entityType}</code>
                    {r.entityId && <small style={{ color: "var(--ink-4)", display: "block" }}>{r.entityId.slice(0, 8)}…</small>}
                  </td>
                  <td style={td} data-label={t("admin.auditLog.col.ip", "IP")}>{r.ipAddress ?? "—"}</td>
                  <td style={td} data-label={t("admin.auditLog.col.request", "Zahtev")}>{r.requestId ? <code style={{ fontSize: 10 }}>{r.requestId.slice(0, 8)}</code> : "—"}</td>
                  <td className="card-actions" style={td}>
                    {(r.beforeData || r.afterData) && (
                      <button type="button" onClick={() => setOpenRow(r)} style={linkBtn}>{t("admin.auditLog.diff", "Razlika")}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />

      {openRow && <DiffModal row={openRow} onClose={() => setOpenRow(null)} />}
    </div>
  );
}

function DiffModal({ row, onClose }: { row: AuditLogRow; onClose: () => void }) {
  const { t } = useTranslation();
  useScrollLock();
  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-primary)" }}>
            <code style={{ fontSize: 14 }}>{row.entityType}</code> · <ActionBadge action={row.action} />
          </h2>
          <button type="button" onClick={onClose} aria-label={t("action.close", "Zatvori")} style={{ all: "unset", cursor: "pointer", color: "var(--ink-3)", fontSize: 20 }}>×</button>
        </header>
        <small style={{ color: "var(--ink-3)" }}>
          {new Date(row.createdAt).toLocaleString("sr-Latn-RS")} · {row.actorName ?? t("admin.auditLog.system", "sistem")}
        </small>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <DataPanel title={t("admin.auditLog.before", "Pre")} json={row.beforeData} tone="neutral" />
          <DataPanel title={t("admin.auditLog.after", "Posle")} json={row.afterData} tone="positive" />
        </div>
      </div>
    </div>
  );
}

function DataPanel({ title, json, tone }: { title: string; json: string | null; tone: "neutral" | "positive" }) {
  let pretty = "—";
  if (json) {
    try {
      pretty = JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      pretty = json;
    }
  }
  const bg = tone === "positive" ? "rgba(31,138,86,.10)" : "var(--surface-2)";
  return (
    <div>
      <h3 style={subTitle}>{title}</h3>
      <pre style={{ ...pre, background: bg }}>{pretty}</pre>
    </div>
  );
}

function ActionBadge({ action }: { action: AuditAction }) {
  const palette: Record<string, { bg: string; fg: string }> = {
    CREATE: { bg: "rgba(31,138,86,.12)", fg: "var(--ok)" },
    UPDATE: { bg: "rgba(31,111,178,.14)", fg: "var(--info)" },
    DELETE: { bg: "rgba(182,53,58,.16)", fg: "var(--err)" },
    LOGIN: { bg: "rgba(14,116,144,.14)", fg: "var(--info)" },
    LOGOUT: { bg: "var(--surface-2)", fg: "var(--ink-3)" },
    LOGIN_FAILED: { bg: "rgba(185,122,14,.16)", fg: "var(--warn)" },
    INVITE: { bg: "rgba(109,40,217,.14)", fg: "var(--info)" },
    PASSWORD_RESET_REQUEST: { bg: "rgba(185,122,14,.16)", fg: "var(--warn)" },
    PASSWORD_RESET_COMPLETE: { bg: "rgba(31,138,86,.12)", fg: "var(--ok)" },
    TOKEN_REFRESH: { bg: "var(--surface-2)", fg: "var(--ink-3)" },
    ACCOUNT_LOCKED: { bg: "rgba(182,53,58,.12)", fg: "var(--err)" },
    ACCOUNT_UNLOCKED: { bg: "rgba(31,138,86,.12)", fg: "var(--ok)" },
  };
  const c = palette[action] ?? { bg: "var(--surface-2)", fg: "var(--ink-3)" };
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
      {action}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, flex: 1, minWidth: 140 }}>
      <span style={{ color: "var(--ink-3)" }}>{label}</span>
      {children}
    </label>
  );
}

const filterRow: React.CSSProperties = { display: "flex", gap: 12, flexWrap: "wrap" };
const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 12, background: "var(--surface)", borderRadius: 8, overflow: "hidden", boxShadow: "0 1px 2px rgba(0,0,0,.04)" };
const th: React.CSSProperties = { textAlign: "left", padding: "8px 10px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid var(--surface-2)", verticalAlign: "top" };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-primary)", cursor: "pointer", fontSize: 12 };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 };
const dialog: React.CSSProperties = { width: "100%", maxWidth: 900, background: "var(--surface)", padding: 24, borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,.18)", display: "flex", flexDirection: "column", gap: 14, maxHeight: "90vh", overflowY: "auto" };
const subTitle: React.CSSProperties = { margin: "0 0 6px", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 };
const pre: React.CSSProperties = { margin: 0, padding: 12, fontSize: 11, fontFamily: "ui-monospace, monospace", borderRadius: 6, overflow: "auto", maxHeight: 400 };
